/**
 * «Sin tomate»: elegir los ingredientes que se quitan de un plato (2026-10-27).
 *
 * Lo pidió el propietario para el mesero: «una opción para quitar ingredientes,
 * tipo una hamburguesa sin tomate». Lo usan la cuenta de la mesa, la ventana de
 * extras (mesa y caja) y el carrito de la caja.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ NO SE INTERPONE AL APUNTAR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Tocar un plato lo sigue apuntando de un toque. «Sin tomate» es la excepción
 * de la noche, no la regla: se hace después, sobre la línea («Quitar
 * ingredientes»), o en la ventana de extras cuando esa ventana ya iba a salir.
 * Una ventana más en cada plato es lo que devuelve a un mesero a la libreta.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LOS BOTONES NO CAMBIAN DE TAMAÑO AL TOCARLOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Si «Tomate» pasara a decir «Sin tomate», el botón crecería y empujaría al de
 * al lado justo cuando el dedo va hacia él: se tocaría el que no era. El texto
 * es siempre el mismo; lo quitado se ve por el color, el tachado y la cruz.
 */

import { useEffect, useState } from "react";
import { CircleMinus, Minus, Plus, X } from "lucide-react";
import { Boton, cx } from "@/components/ui";
import { Modal } from "@/components/Modal";
import type { IngredienteQuitado, QuitableDeLista } from "@/lib/types";

/** Los botones de cada ingrediente. Tocar uno lo quita; tocarlo otra vez, lo devuelve. */
export function ChipsDeSin({
  quitables,
  quitados,
  onAlternar,
}: {
  quitables: QuitableDeLista[];
  quitados: ReadonlySet<string>;
  onAlternar: (variantId: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {quitables.map((q) => {
        const fuera = quitados.has(q.variant_id);
        return (
          <button
            key={q.variant_id}
            type="button"
            aria-pressed={fuera}
            aria-label={`Sin ${q.nombre}`}
            onClick={() => onAlternar(q.variant_id)}
            className={cx(
              // 44 px de alto, como los extras: se toca de pie y con prisa.
              "inline-flex min-h-11 items-center gap-1.5 rounded-xl border px-3 py-2 text-left text-sm transition-colors",
              fuera
                ? "border-danger/50 bg-danger-soft font-medium text-danger"
                : "border-line text-ink hover:border-line-strong",
            )}
          >
            {fuera ? (
              <X size={14} className="shrink-0" aria-hidden />
            ) : (
              <Minus size={14} className="shrink-0 text-muted" aria-hidden />
            )}
            <span className={cx(fuera && "line-through decoration-2")}>{q.nombre}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Lo elegido, con su nombre, en el orden de la receta mostrada. */
export function quitadosConNombre(
  quitables: QuitableDeLista[],
  quitados: ReadonlySet<string>,
): IngredienteQuitado[] {
  return quitables
    .filter((q) => quitados.has(q.variant_id))
    .map((q) => ({ variant_id: q.variant_id, nombre: q.nombre }));
}

/**
 * La ventana para quitar ingredientes de una línea ya apuntada.
 *
 * `cantidad` > 1 solo pasa en la caja, donde dos hamburguesas iguales van en
 * una línea: ahí se pregunta a cuántas se aplica, y la caja separa la línea
 * («1 sin tomate» y «1 completa»). En la mesa cada plato es su propia línea.
 */
export function ModalDeSin({
  abierto,
  plato,
  quitables,
  inicial,
  cantidad = 1,
  yaEnviado = false,
  onGuardar,
  onCerrar,
}: {
  abierto: boolean;
  plato: string;
  quitables: QuitableDeLista[];
  /** Lo que ya tenía quitado: esto sirve para poner y para corregir. */
  inicial: readonly IngredienteQuitado[];
  cantidad?: number;
  yaEnviado?: boolean;
  onGuardar: (sin: IngredienteQuitado[], cuantas: number) => void;
  onCerrar: () => void;
}) {
  const [quitados, setQuitados] = useState<Set<string>>(new Set());
  const [cuantas, setCuantas] = useState(cantidad);
  const clave = inicial.map((q) => q.variant_id).join(",");

  // Al abrir se parte de lo que tenía, como la nota: empezar en blanco borraría
  // sin avisar lo que se había quitado antes.
  useEffect(() => {
    if (!abierto) return;
    setQuitados(new Set(clave ? clave.split(",") : []));
    setCuantas(cantidad);
  }, [abierto, clave, cantidad]);

  function alternar(id: string) {
    setQuitados((previos) => {
      const copia = new Set(previos);
      if (copia.has(id)) copia.delete(id);
      else copia.add(id);
      return copia;
    });
  }

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Quitar ingredientes"
      descripcion={plato}
      ancho="sm"
      pie={
        <>
          <Boton variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton onClick={() => onGuardar(quitadosConNombre(quitables, quitados), cuantas)}>
            Guardar
          </Boton>
        </>
      }
    >
      <ChipsDeSin quitables={quitables} quitados={quitados} onAlternar={alternar} />
      <p className="mt-3 text-xs text-muted">
        Lo seleccionado no se prepara ni se descuenta del inventario.
      </p>

      {cantidad > 1 && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-line px-3 py-2">
          <span className="text-sm text-ink">Aplicar a</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCuantas((n) => Math.max(1, n - 1))}
              disabled={cuantas <= 1}
              aria-label="Una menos"
              className="grid size-9 place-items-center rounded-lg border border-line text-ink disabled:opacity-40"
            >
              <Minus size={15} />
            </button>
            <span className="min-w-16 text-center text-sm font-medium tabular text-ink">
              {cuantas} de {cantidad}
            </span>
            <button
              type="button"
              onClick={() => setCuantas((n) => Math.min(cantidad, n + 1))}
              disabled={cuantas >= cantidad}
              aria-label="Una más"
              className="grid size-9 place-items-center rounded-lg border border-line text-ink disabled:opacity-40"
            >
              <Plus size={15} />
            </button>
          </div>
        </div>
      )}

      {yaEnviado && (
        <p className="mt-3 text-xs text-warning">
          Este plato ya está en preparación. El cambio aparece al momento en la pantalla de
          cocina; si ya comenzaron, se recomienda avisarles.
        </p>
      )}
    </Modal>
  );
}

/**
 * El botón de la línea: «Quitar ingredientes», o lo quitado («Sin Tomate ·
 * Sin Cebolla») en rojo, que se toca para corregirlo.
 *
 * En rojo y en su propia línea por lo mismo que la nota: es lo que, si se pasa
 * por alto, arruina el plato.
 */
export function BotonDeSin({
  sin,
  onAbrir,
}: {
  sin: readonly IngredienteQuitado[];
  onAbrir: () => void;
}) {
  const hay = sin.length > 0;
  return (
    <button
      type="button"
      onClick={onAbrir}
      className={cx(
        "flex max-w-full items-center gap-1 rounded-md px-1.5 py-0.5 text-xs transition-colors",
        hay
          ? "bg-danger-soft font-semibold text-danger hover:brightness-95"
          : "text-muted hover:bg-surface-soft hover:text-accent",
      )}
    >
      <CircleMinus size={12} className="shrink-0" aria-hidden />
      <span className="truncate">
        {hay ? sin.map((q) => `Sin ${q.nombre}`).join(" · ") : "Quitar ingredientes"}
      </span>
    </button>
  );
}
